import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const location = useLocation();
  const {
    lang,
    setLang,
    t
  } = useLanguage();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSolutionsOpen(false);
  }, [location.pathname]);
  const navLinks = [{
    name: t('nav.home'),
    path: '/'
  }, {
    name: t('nav.catalog'),
    path: '/catalogo'
  }, {
    name: t('nav.downloads'),
    path: '/descargables'
  }, {
    name: t('nav.company'),
    path: '/empresa'
  }];
  const solutionLinks = [{
    name: t('nav.seeAll'),
    path: '/soluciones'
  }, {
    name: 'SmartFloor',
    path: '/soluciones/smartfloor'
  }, {
    name: 'SmartPanel',
    path: '/soluciones/smartpanel'
  }, {
    name: 'SmartPanel Exterior & Decorativo',
    path: '/soluciones/smartstrips'
  }, {
    name: 'SmartDoor',
    path: '/soluciones/smartdoor'
  }, {
    name: 'SmartSofa',
    path: '/soluciones/smartsofa'
  }, {
    name: 'SmartMachinery',
    path: '/soluciones/smartmachinery'
  }, {
    name: 'SmartGarden',
    path: '/soluciones/smartgarden'
  }];
  const isHomePage = location.pathname === '/';
  // Background states — always solid white
  const navBgClass = isScrolled ? 'bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)] py-4' : 'bg-white py-6 shadow-[0_2px_8px_rgba(0,0,0,0.05)]';
  // Helper to get link classes based on active state
  const getLinkClass = (isActive: boolean) => {
    return isActive ? 'text-corporate-blue' : 'text-corporate-dark hover:text-corporate-blue';
  };
  // Underline color
  const underlineClass = 'bg-corporate-blue';
  // Mobile hamburger color
  const hamburgerClass = 'text-corporate-dark hover:text-corporate-blue';
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-premium ${navBgClass}`} data-id="element-85">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-86">
        <div className="flex justify-between items-center" data-id="element-87">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center" data-id="element-88">
            <img src="https://cdn.magicpatterns.com/uploads/dGu33eyB5j7rueds66611Z/Logo_smartimport-1.png" alt="SmartImport Internacional" className="h-[34px] sm:h-10 md:h-[50px] w-auto object-contain transition-all duration-500" data-id="element-89" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-10" data-id="element-90">
            <nav className="flex space-x-10 items-center" data-id="element-91">
              <Link to="/" className={`relative font-heading font-medium text-xs tracking-[0.15em] uppercase transition-colors duration-500 ${getLinkClass(location.pathname === '/')}`} data-id="element-92">
                {t('nav.home')}
                {location.pathname === '/' && <span className={`absolute -bottom-2 left-0 w-full h-0.5 ${underlineClass} transition-all duration-500`} data-id="element-93"></span>}
              </Link>

              {/* Solutions Dropdown */}
              <div className="relative group" data-id="element-94">
                <button className={`relative flex items-center font-heading font-medium text-xs tracking-[0.15em] uppercase transition-colors duration-500 ${getLinkClass(location.pathname.includes('/soluciones'))}`} data-id="element-95">
                  {t('nav.solutions')} <ChevronDown className="ml-1 h-4 w-4" data-id="element-96" />
                  {location.pathname.includes('/soluciones') && <span className={`absolute -bottom-2 left-0 w-full h-0.5 ${underlineClass} transition-all duration-500`} data-id="element-97"></span>}
                </button>
                <div className="absolute left-0 mt-4 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-500 ease-premium transform origin-top-left scale-95 group-hover:scale-100" data-id="element-98">
                  <div className="bg-white rounded-sm shadow-xl ring-1 ring-black ring-opacity-5 py-2 mt-2 border-t-2 border-accent-beige" data-id="element-99">
                    {solutionLinks.map(link => <Link key={link.name} to={link.path} className="block px-6 py-3 text-sm text-gray-700 hover:bg-neutral-grey hover:text-corporate-blue transition-colors duration-300" data-id="element-100">
                        {link.name}
                      </Link>)}
                  </div>
                </div>
              </div>

              {navLinks.slice(1).map(link => <Link key={link.name} to={link.path} className={`relative font-heading font-medium text-xs tracking-[0.15em] uppercase transition-colors duration-500 ${getLinkClass(location.pathname === link.path)}`} data-id="element-101">
                  {link.name}
                  {location.pathname === link.path && <span className={`absolute -bottom-2 left-0 w-full h-0.5 ${underlineClass} transition-all duration-500`} data-id="element-102"></span>}
                </Link>)}
            </nav>

            {/* Language Switcher */}
            <div className="flex items-center space-x-2 border-l border-gray-300/30 pl-6" data-id="element-103">
              <button onClick={() => setLang('es')} className={`font-heading font-medium text-xs tracking-[0.15em] transition-colors duration-300 ${lang === 'es' ? 'text-corporate-blue' : 'text-gray-400 hover:text-corporate-dark'}`} data-id="element-104">
                ES
              </button>
              <span className="text-gray-300" data-id="element-105">|</span>
              <button onClick={() => setLang('en')} className={`font-heading font-medium text-xs tracking-[0.15em] transition-colors duration-300 ${lang === 'en' ? 'text-corporate-blue' : 'text-gray-400 hover:text-corporate-dark'}`} data-id="element-106">
                EN
              </button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-6" data-id="element-107">
            {/* Mobile Lang Switcher */}
            <div className="flex items-center space-x-2" data-id="element-108">
              <button onClick={() => setLang('es')} className={`font-heading font-medium text-xs tracking-[0.15em] ${lang === 'es' ? 'text-corporate-blue' : 'text-gray-400'}`} data-id="element-109">
                ES
              </button>
              <span className="text-gray-300" data-id="element-110">|</span>
              <button onClick={() => setLang('en')} className={`font-heading font-medium text-xs tracking-[0.15em] ${lang === 'en' ? 'text-corporate-blue' : 'text-gray-400'}`} data-id="element-111">
                EN
              </button>
            </div>

            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className={`${hamburgerClass} focus:outline-none transition-colors duration-500`} data-id="element-112">
              {isMobileMenuOpen ? <X className="h-6 w-6" data-id="element-113" /> : <Menu className="h-6 w-6" data-id="element-114" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Drawer */}
      <div className={`md:hidden absolute top-full left-0 w-full bg-white shadow-xl transition-all duration-500 ease-premium overflow-hidden border-t border-accent-beige/30 ${isMobileMenuOpen ? 'max-h-[600px]' : 'max-h-0'}`} data-id="element-115">
        <div className="px-4 pt-4 pb-8 space-y-2" data-id="element-116">
          <Link to="/" className="block px-4 py-4 text-sm tracking-[0.1em] uppercase font-heading font-medium text-corporate-dark hover:bg-neutral-grey rounded-sm transition-colors duration-300" data-id="element-117">
            {t('nav.home')}
          </Link>

          <div data-id="element-118">
            <button onClick={() => setIsSolutionsOpen(!isSolutionsOpen)} className="w-full flex justify-between items-center px-4 py-4 text-sm tracking-[0.1em] uppercase font-heading font-medium text-corporate-dark hover:bg-neutral-grey rounded-sm transition-colors duration-300" data-id="element-119">
              {t('nav.solutions')}
              <ChevronDown className={`h-5 w-5 transition-transform duration-500 ${isSolutionsOpen ? 'rotate-180' : ''}`} data-id="element-120" />
            </button>
            <div className={`pl-8 space-y-1 overflow-hidden transition-all duration-500 ease-premium ${isSolutionsOpen ? 'max-h-[500px] mt-2 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-121">
              {solutionLinks.map(link => <Link key={link.name} to={link.path} className="block px-4 py-3 text-sm text-gray-600 hover:text-corporate-blue transition-colors duration-300" data-id="element-122">
                  {link.name}
                </Link>)}
            </div>
          </div>

          {navLinks.slice(1).map(link => <Link key={link.name} to={link.path} className="block px-4 py-4 text-sm tracking-[0.1em] uppercase font-heading font-medium text-corporate-dark hover:bg-neutral-grey rounded-sm transition-colors duration-300" data-id="element-123">
              {link.name}
            </Link>)}
        </div>
      </div>
    </header>;
}